import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { FieldDef } from 'app/core/models';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { FieldDefsState } from 'app/state/fields/state';
import { ActionType, ActionsSetupArgs, Automation } from '../../../builder.types';
import { Dictionary } from 'app/shared/models';
import { Observable, delay, noop, tap } from 'rxjs';
import { FormBase } from '../builder-form.types';
import { AutomationState } from 'app/modules/settings/automations/state/state';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { Automation as AutomationActions } from 'app/modules/settings/automations/state/actions';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';

@Component({
  selector: 'app-automation-builder-action-setup-form',
  templateUrl: './action-setup.component.html',
  styleUrls: ['./action-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionSetupFormComponent extends FormBase<ActionsSetupArgs> implements OnInit {
  @Input() set id(value: number) {
    if (value) {
      const action = this.automation.actions.find(({ id }) => id === value);
      this.form.setValue({
        id: value,
        type: action.type,
        objectFieldDef:
          action.type === ActionType.CreateUnderwritingModel
            ? null
            : action.attr
            ? this.fieldDefs.find(
                ({ name, related_to_type }) => name === action.attr && related_to_type === this.automation.target_model,
              )
            : null,
        value:
          action.type === ActionType.CreateUnderwritingModel ? (action.value ? +action.value : null) : action.value,
      });

      if (action.type === ActionType.SetAttribute && action.attr) {
        let value = +action.value;
        if (isNaN(value)) {
          value = action.value;
        }
        this.model = { [action.attr]: value };
      }

      this.form.get('type').updateValueAndValidity();
      this.form.get('objectFieldDef').updateValueAndValidity();
      this.form.get('value').updateValueAndValidity();
    }
  }
  @Input() formData: any;

  objects: CustomObject[];
  fieldDefs: FieldDef[];
  model: any = {};
  form = new FormGroup({
    id: new FormControl(null, [Validators.required]),
    type: new FormControl(null, [Validators.required]),
    objectFieldDef: new FormControl(null, [Validators.required]),
    value: new FormControl(null, [Validators.required]),
  });
  updatePointer = true;
  ActionType = ActionType;

  @Select(AutomationState.getEligibleFiles) eligibleFiles$: Observable<FileResource[]>;

  constructor(private readonly store: Store) {
    super();
    this.objects = this.store.selectSnapshot(ObjectsManagerState.get);
    this.fieldDefs = this.store.selectSnapshot(FieldDefsState.getFieldDefs);
  }

  ngOnInit(): void {
    // this is temporary, the way of adding values is going to change soon
    this.form
      .get('objectFieldDef')
      .valueChanges.pipe(
        untilComponentDestroyed(this),
        tap(() => (this.updatePointer = false)),
        delay(100),
        tap(() => (this.updatePointer = true)),
      )
      .subscribe(noop);

    this.form
      .get('type')
      .valueChanges.pipe(
        untilComponentDestroyed(this),
        tap((value: ActionType) => {
          this.form.get('value').setValue(null);

          if (value === ActionType.SetAttribute) {
            this.form.get('objectFieldDef').setValidators([Validators.required]);
            this.form.get('objectFieldDef').updateValueAndValidity();
          } else if (value === ActionType.CreateUnderwritingModel) {
            this.form.get('objectFieldDef').setValidators(null);
            this.form.get('objectFieldDef').updateValueAndValidity();
          }
        }),
      )
      .subscribe(noop);

    this.store.dispatch(new AutomationActions.GetEligibleFiles());
  }

  onFieldUpdate(model: Dictionary<any>, objectValueSetter: FormGroup = null) {
    if (objectValueSetter) {
      objectValueSetter.get('value').setValue(model[objectValueSetter.get('objectFieldDef').value.name]);
    } else {
      this.form.get('value').setValue(model[this.form.get('objectFieldDef').value.name]);
      this.model = { ...model };
    }
  }

  save(): void {
    const formValue = this.form.getRawValue();

    if (formValue.type === ActionType.SetAttribute) {
      this.ok.emit({
        attr: formValue.objectFieldDef.name,
        type: ActionType.SetAttribute,
        value: formValue.value,
        id: formValue.id as number,
      });
    } else if (formValue.type === ActionType.CreateUnderwritingModel) {
      this.ok.emit({
        attr: 'FileResource',
        type: ActionType.CreateUnderwritingModel,
        value: formValue.value,
        id: formValue.id as number,
      });
    }
  }
}
